import { useNavigate } from "react-router-dom"
import { useContext, useState } from "react"
import { useFormikContext } from "formik"
import { ButtonGroup, Button, CircularProgress } from "@mui/material"
import { FlowHandlersContext } from "../../FlowContainer"
import styles from "./styles"

function buildTouchedFromErrors(errors) {
    if (!errors || typeof errors !== "object") {
        return true
    }

    if (Array.isArray(errors)) {
        return errors.map((item) => buildTouchedFromErrors(item))
    }

    return Object.keys(errors).reduce((acc, key) => {
        acc[key] = buildTouchedFromErrors(errors[key])
        return acc
    }, {})
}

export default function NavButtons(props) {
    // console.log("NavButtons rendering with props:", props)
    const navigate = useNavigate()
    const formik = useFormikContext()
    const handlers = useContext(FlowHandlersContext)
    const [loading, setLoading] = useState(false);
    const [loadingExit, setLoadingExit] = useState(false);
    const { mode, exitUrl, enableSave, enableBack, hideNext, children, backLabel, nextLabel, step, disabled, isSysAdmin } = props
    const { onNextStep: customOnNextStep, onBackStep: customOnBackStep } = props
    // console.log("NavButtons: handlers from context =", handlers)

    async function defaultOnBackStep(event) {
        event.preventDefault()

        // Use handler from context if available
        if (handlers?.onBack) {
            await handlers.onBack()
        }
    }

    async function defaultOnNextStep(event) {
        event.preventDefault()
        setLoading(true);
        if (formik && formik.submitForm) {
            // console.log("NavButtons: Validating form before next step")
            const errors = await formik.validateForm()

            if (Object.keys(errors).length === 0) {
                await formik.submitForm()

                // After submission completes, navigate using handler
                if (handlers?.onNext) {
                    await handlers.onNext(formik.values)
                }
            } else {
                formik.setTouched(buildTouchedFromErrors(errors), true)
            }
        } else {
            console.log("NavButtons: No formik context, proceeding to next step")
            // No formik, just navigate
            if (handlers?.onNext) {
                await handlers.onNext()
            }
        }
        setLoading(false);
    }
    async function defaultExitStep(event) {
        event.preventDefault()
        setLoadingExit(true);
        navigate(exitUrl)
        setLoadingExit(false);
    }
    async function defaultSaveStep(event) {
        event.preventDefault()
        setLoadingExit(true);
        if (formik && formik.submitForm) {
            const errors = await formik.validateForm()

            if (Object.keys(errors).length === 0) {
                await formik.submitForm()
                navigate(exitUrl)
            } else {
                formik.setTouched(buildTouchedFromErrors(errors), true)
            }
        }
        setLoadingExit(false);
    }

    async function handleBackStep(event) {
        if (customOnBackStep && typeof customOnBackStep === 'function') {
            event.preventDefault()
            setLoading(true)
            try {
                await Promise.resolve(customOnBackStep(event))
            } finally {
                setLoading(false)
            }
            return
        }

        // Fallback to the default behavior which already manages loading
        await defaultOnBackStep(event)
    }

    async function handleNextStep(event) {
        // If a custom handler is provided, ensure we show the loading state while it runs
        if (customOnNextStep && typeof customOnNextStep === 'function') {
            event.preventDefault()
            setLoading(true)
            try {
                await Promise.resolve(customOnNextStep(event))
            } finally {
                setLoading(false)
            }
            return
        }

        // Fallback to the default behavior which already manages loading
        await defaultOnNextStep(event)
    }

    return (
        <ButtonGroup sx={styles.container}>
            {step > 0 && enableBack && (
                <Button type="button" variant="outlined" onClick={handleBackStep} sx={styles.button} disabled={loading} aria-label="back-step">
                    {loading ? <CircularProgress size={24} sx={{".MuiCircularProgress-circle":{ color: "#fff"}}} />  : (backLabel || "Back")}
                </Button>
            )}

            {!hideNext && (
                <Button type="submit" variant="contained" onClick={handleNextStep} sx={styles.button} disabled={disabled || loading} aria-label="next-step">
                    {loading ? <CircularProgress size={24} sx={{".MuiCircularProgress-circle":{ color: "#fff"}}} />  : (nextLabel || "Next")}
                </Button>
            )}

            {children}

            {mode === "manage" && enableSave && exitUrl?.length > 0 && (
                <Button
                    type="button"
                    variant="outlined"
                    sx={styles.exitButton}
                    onClick={defaultSaveStep}
                    aria-label="manage-save"
                    disabled={loadingExit}
                >
                    {loadingExit ? <CircularProgress size={24} />  : "Save & Exit"}
                </Button>
            )}
            {mode === "manage" && !enableSave && exitUrl?.length > 0 && (
                <Button
                    type="button"
                    variant="outlined"
                    sx={styles.exitButton}
                    onClick={defaultExitStep}
                    aria-label="manage-exit"
                    disabled={loadingExit}
                >
                    {loadingExit ? <CircularProgress size={24} />  : "Exit"}
                </Button>
            )}
            {mode === "add" && isSysAdmin && exitUrl?.length > 0 && (
                <Button
                    type="button"
                    variant="outlined"
                    sx={styles.exitButton}
                    onClick={defaultExitStep}
                    aria-label="add-exit"
                    disabled={loadingExit}
                >
                    {loadingExit ? <CircularProgress size={24} />  : "Exit"}
                </Button>
            )}
        </ButtonGroup>
    )
}